/**
 * Маска прав и алгоритм эффективных прав в канале — копия серверного
 * internal/permissions (спецификация 6.3). Маски приходят строками, считаем в BigInt.
 */
import type { Channel, Guild, Member, Role } from '@/types/api'

export const P = {
  CREATE_INSTANT_INVITE: 1n << 0n,
  KICK_MEMBERS: 1n << 1n,
  BAN_MEMBERS: 1n << 2n,
  ADMINISTRATOR: 1n << 3n,
  MANAGE_CHANNELS: 1n << 4n,
  MANAGE_GUILD: 1n << 5n,
  ADD_REACTIONS: 1n << 6n,
  VIEW_AUDIT_LOG: 1n << 7n,
  PRIORITY_SPEAKER: 1n << 8n,
  STREAM: 1n << 9n,
  VIEW_CHANNEL: 1n << 10n,
  SEND_MESSAGES: 1n << 11n,
  SEND_TTS_MESSAGES: 1n << 12n,
  MANAGE_MESSAGES: 1n << 13n,
  MANAGE_GUILD_EXPRESSIONS: 1n << 30n,
  EMBED_LINKS: 1n << 14n,
  ATTACH_FILES: 1n << 15n,
  READ_MESSAGE_HISTORY: 1n << 16n,
  MENTION_EVERYONE: 1n << 17n,
  USE_EXTERNAL_EMOJIS: 1n << 18n,
  VIEW_GUILD_INSIGHTS: 1n << 19n,
  CONNECT: 1n << 20n,
  SPEAK: 1n << 21n,
  MUTE_MEMBERS: 1n << 22n,
  DEAFEN_MEMBERS: 1n << 23n,
  MOVE_MEMBERS: 1n << 24n,
  USE_VAD: 1n << 25n,
  CHANGE_NICKNAME: 1n << 26n,
  MANAGE_NICKNAMES: 1n << 27n,
  MANAGE_ROLES: 1n << 28n,
  MANAGE_WEBHOOKS: 1n << 29n,
  MANAGE_EXPRESSIONS: 1n << 30n,
  USE_APPLICATION_COMMANDS: 1n << 31n,
  REQUEST_TO_SPEAK: 1n << 32n,
  MANAGE_EVENTS: 1n << 33n,
  MANAGE_THREADS: 1n << 34n,
  CREATE_PUBLIC_THREADS: 1n << 35n,
  CREATE_PRIVATE_THREADS: 1n << 36n,
  USE_EXTERNAL_STICKERS: 1n << 37n,
  SEND_MESSAGES_IN_THREADS: 1n << 38n,
  MODERATE_MEMBERS: 1n << 40n,
  USE_SOUNDBOARD: 1n << 42n,
  CREATE_EXPRESSIONS: 1n << 43n,
  CREATE_EVENTS: 1n << 44n,
  USE_EXTERNAL_SOUNDS: 1n << 45n,
  SEND_VOICE_MESSAGES: 1n << 46n,
  SEND_POLLS: 1n << 49n,
} as const

export type PermissionKey = keyof typeof P

export const ALL = Object.values(P).reduce((acc, bit) => acc | bit, 0n)

export function bits(mask: string | undefined | null): bigint {
  if (!mask) return 0n
  try {
    return BigInt(mask)
  } catch {
    return 0n
  }
}

export function has(mask: bigint, p: bigint): boolean {
  return (mask & p) === p
}

/** Базовые права участника на сервере: @everyone | роли; владелец и администратор — всё. */
export function basePermissions(guild: Pick<Guild, 'id' | 'owner_id' | 'roles'>, member: Pick<Member, 'roles'> & { user: { id: string } }): bigint {
  if (guild.owner_id === member.user.id) return ALL
  const byId = new Map(guild.roles.map((r) => [r.id, r]))
  let p = bits(byId.get(guild.id)?.permissions)
  for (const rid of member.roles) p |= bits(byId.get(rid)?.permissions)
  if (has(p, P.ADMINISTRATOR)) return ALL
  return p
}

/** Эффективные права в канале с учётом перекрытий (алгоритм Discord). */
export function channelPermissions(
  guild: Pick<Guild, 'id' | 'owner_id' | 'roles'>,
  member: Pick<Member, 'roles'> & { user: { id: string } },
  channel: Pick<Channel, 'permission_overwrites'> | null | undefined,
): bigint {
  let p = basePermissions(guild, member)
  if (p === ALL || !channel) return p
  const ows = channel.permission_overwrites ?? []
  const find = (type: 0 | 1, id: string) => ows.find((o) => o.type === type && o.id === id)
  const everyone = find(0, guild.id)
  if (everyone) p = (p & ~bits(everyone.deny)) | bits(everyone.allow)
  let allow = 0n
  let deny = 0n
  for (const rid of member.roles) {
    const ow = find(0, rid)
    if (ow) {
      allow |= bits(ow.allow)
      deny |= bits(ow.deny)
    }
  }
  p = (p & ~deny) | allow
  const mine = find(1, member.user.id)
  if (mine) p = (p & ~bits(mine.deny)) | bits(mine.allow)
  if (!has(p, P.VIEW_CHANNEL)) return 0n
  return p
}

/** Права в ветке: как в родительском канале, а писать разрешает SEND_MESSAGES_IN_THREADS. */
export function threadPermissions(
  guild: Pick<Guild, 'id' | 'owner_id' | 'roles'>,
  member: Pick<Member, 'roles'> & { user: { id: string } },
  parent: Pick<Channel, 'permission_overwrites'> | null | undefined,
): bigint {
  const p = channelPermissions(guild, member, parent)
  if (p === ALL) return p
  return has(p, P.SEND_MESSAGES_IN_THREADS) ? p | P.SEND_MESSAGES : p & ~P.SEND_MESSAGES
}

/** Высшая роль участника — для цвета имени и иерархии. */
export function topRole(roles: Role[], memberRoles: string[]): Role | undefined {
  let best: Role | undefined
  for (const r of roles) {
    if (memberRoles.includes(r.id) && (!best || r.position > best.position)) best = r
  }
  return best
}

/** Почему роль под замком: 'top' — это своя самая высокая роль, 'higher' — роль выше неё. */
export type RoleLock = 'top' | 'higher'

/**
 * Замок на роли для участника self; null — роль можно менять и выдавать. Как у Discord: трогать
 * можно только роли строго ниже своей самой высокой, и себе тоже; владельцу сервера — любые.
 * @everyone в иерархии не участвует.
 */
export function roleLock(guild: Pick<Guild, 'id' | 'owner_id' | 'roles'>, self: Pick<Member, 'roles'> & { user: { id: string } }, role: Pick<Role, 'id' | 'position'>): RoleLock | null {
  if (guild.owner_id === self.user.id || role.id === guild.id) return null
  const top = topRole(guild.roles, self.roles)
  if (top?.id === role.id) return 'top'
  return role.position >= (top?.position ?? 0) ? 'higher' : null
}

/** Подсказка у замка в списке ролей: правка самой роли. */
export const ROLE_LOCK_EDIT: Record<RoleLock, string> = {
  top: 'Это ваша самая высокая роль: её меняет только тот, чья роль выше, или владелец сервера',
  higher: 'Роль выше вашей самой высокой',
}

/** Подсказка у замка в ролях участника: выдать или снять. */
export const ROLE_LOCK_ASSIGN: Record<RoleLock, string> = {
  top: 'Это ваша самая высокая роль: её выдаёт и снимает только тот, чья роль выше, или владелец сервера',
  higher: 'Роль выше вашей самой высокой',
}

/** Роль бота выдаёт и снимает само приложение — вручную её не трогают даже владельцы. */
export const MANAGED_ROLE_HINT = 'Роль бота: её выдаёт и снимает приложение'

/** Почему участнику нельзя выдать или снять роль; undefined — можно. */
export function assignLockHint(guild: Pick<Guild, 'id' | 'owner_id' | 'roles'>, self: Pick<Member, 'roles'> & { user: { id: string } }, role: Pick<Role, 'id' | 'position' | 'managed'>): string | undefined {
  if (role.managed) return MANAGED_ROLE_HINT
  const lock = roleLock(guild, self, role)
  return lock ? ROLE_LOCK_ASSIGN[lock] : undefined
}

/** Цвет имени: первая по позиции роль с цветом. */
export function roleColor(roles: Role[], memberRoles: string[]): string | undefined {
  const colored = roles.filter((r) => r.color !== 0 && memberRoles.includes(r.id)).sort((a, b) => b.position - a.position)
  const r = colored[0]
  return r ? `#${r.color.toString(16).padStart(6, '0')}` : undefined
}

/** Права для формы ролей и перекрытий, сгруппированы как в Discord. */
export const PERMISSION_GROUPS: { title: string; items: { key: PermissionKey; label: string; hint: string }[] }[] = [
  {
    title: 'Общие права сервера',
    items: [
      { key: 'VIEW_CHANNEL', label: 'Просматривать каналы', hint: 'Видеть каналы по умолчанию (кроме приватных)' },
      { key: 'MANAGE_CHANNELS', label: 'Управлять каналами', hint: 'Создавать, изменять и удалять каналы' },
      { key: 'MANAGE_ROLES', label: 'Управлять ролями', hint: 'Создавать роли ниже своей и менять права' },
      { key: 'MANAGE_GUILD', label: 'Управлять сервером', hint: 'Название, системный канал, приглашения' },
      { key: 'CREATE_INSTANT_INVITE', label: 'Создавать приглашения', hint: '' },
      { key: 'CHANGE_NICKNAME', label: 'Изменять свой ник', hint: '' },
      { key: 'MANAGE_NICKNAMES', label: 'Управлять никами', hint: 'Менять ники других участников' },
      { key: 'KICK_MEMBERS', label: 'Выгонять участников', hint: '' },
      { key: 'BAN_MEMBERS', label: 'Банить участников', hint: '' },
      { key: 'MODERATE_MEMBERS', label: 'Отправлять в таймаут', hint: 'До 28 дней без сообщений' },
      { key: 'ADMINISTRATOR', label: 'Администратор', hint: 'Все права; перекрытия каналов не действуют' },
    ],
  },
  {
    title: 'Текстовые каналы',
    items: [
      { key: 'SEND_MESSAGES', label: 'Отправлять сообщения', hint: '' },
      { key: 'READ_MESSAGE_HISTORY', label: 'Читать историю', hint: '' },
      { key: 'EMBED_LINKS', label: 'Встраивать ссылки', hint: '' },
      { key: 'ATTACH_FILES', label: 'Прикреплять файлы', hint: '' },
      { key: 'ADD_REACTIONS', label: 'Добавлять реакции', hint: '' },
      { key: 'SEND_POLLS', label: 'Создавать опросы', hint: '' },
      { key: 'SEND_VOICE_MESSAGES', label: 'Отправлять голосовые сообщения', hint: '' },
      { key: 'MENTION_EVERYONE', label: 'Упоминать @everyone и все роли', hint: '' },
      { key: 'MANAGE_MESSAGES', label: 'Управлять сообщениями', hint: 'Удалять чужие сообщения, закреплять, снимать реакции' },
      { key: 'MANAGE_GUILD_EXPRESSIONS', label: 'Управлять эмодзи, стикерами и звуками', hint: 'Добавлять, править и удалять, в том числе чужие звуки' },
      { key: 'CREATE_EXPRESSIONS', label: 'Добавлять звуки', hint: 'Загружать звуки звуковой панели и править свои' },
      { key: 'MANAGE_WEBHOOKS', label: 'Управлять вебхуками', hint: 'Создавать и удалять вебхуки: сообщения от внешних сервисов (CI, GitHub)' },
    ],
  },
  {
    title: 'Голосовые каналы',
    items: [
      { key: 'CONNECT', label: 'Подключаться', hint: '' },
      { key: 'SPEAK', label: 'Говорить', hint: '' },
      { key: 'STREAM', label: 'Демонстрировать экран', hint: '' },
      { key: 'USE_SOUNDBOARD', label: 'Звуковая панель', hint: 'Играть звуки в голосовом канале' },
      { key: 'USE_EXTERNAL_SOUNDS', label: 'Звуки с других серверов', hint: 'Для тех, у кого Кличат Плюс' },
      { key: 'MUTE_MEMBERS', label: 'Отключать микрофон другим', hint: '' },
      { key: 'DEAFEN_MEMBERS', label: 'Отключать звук другим', hint: '' },
      { key: 'MOVE_MEMBERS', label: 'Перемещать участников', hint: 'Перемещать между голосовыми каналами и отключать от голоса' },
    ],
  },
]
